import { useState } from "react";
import type { ReactNode } from "react";
import { RATING_OPTIONS, SORT_OPTIONS } from "@/lib/filmData";

export interface FilterValue {
  year: string;
  region: string;
  rating: string;
  sort: string;
}

interface FilterBarProps {
  years: string[];
  regions: string[];
  value: FilterValue;
  onChange: (key: keyof FilterValue, next: string) => void;
  onReset: () => void;
}

const selectClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 transition-colors focus:border-primary-400 focus:outline-none";

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-foreground-500">{label}</span>
      {children}
    </label>
  );
}

export default function FilterBar({ years, regions, value, onChange, onReset }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const controls = (
    <>
      <Field label="上映年份">
        <select className={selectClass} value={value.year} onChange={(e) => onChange("year", e.target.value)}>
          <option value="all">全部年份</option>
          {years.map((year) => (
            <option key={year} value={year}>
              {year} 年
            </option>
          ))}
        </select>
      </Field>

      <Field label="地区">
        <select className={selectClass} value={value.region} onChange={(e) => onChange("region", e.target.value)}>
          <option value="all">全部地区</option>
          {regions.map((region) => (
            <option key={region} value={region}>
              {region}
            </option>
          ))}
        </select>
      </Field>

      <Field label="评分等级">
        <select className={selectClass} value={value.rating} onChange={(e) => onChange("rating", e.target.value)}>
          {RATING_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="排序方式">
        <select className={selectClass} value={value.sort} onChange={(e) => onChange("sort", e.target.value)}>
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Field>
    </>
  );

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 md:hidden">
        <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
          <i className="ri-filter-3-line text-lg text-primary-500" aria-hidden="true"></i>
          筛选与排序
        </span>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs font-medium text-foreground-700"
        >
          {open ? "收起" : "展开"}
          <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true"></i>
        </button>
      </div>

      <div className={`${open ? "mt-4 grid" : "hidden"} grid-cols-1 gap-4 sm:grid-cols-2 md:mt-0 md:grid md:grid-cols-4`}>
        {controls}
      </div>

      <div className="mt-4 flex items-center justify-end">
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs font-medium text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600"
        >
          <i className="ri-refresh-line" aria-hidden="true"></i>
          重置筛选
        </button>
      </div>
    </div>
  );
}